import Ajax from "ui/helpers/Ajax";
import Spinner from "ui/components/spinner/Spinner";
import FatalError from "ui/components/fatal-error/FatalError";
import Confirm from "ui/dialogs/confirm/Confirm";
import ReleaseForm from "ui/editor/dialogs/release/ReleaseForm";
import MainDb, {STORE_FILES} from "ui/editor/db/MainDb";
import {i18n} from "ui/i18n";
import {generateUuid, isUuid} from "ui/helpers/uuid";
import SceneFlatter from "ui/scene/SceneFlatter";
import {CurrentUser} from "ui/CurrentUser";
import ProgressDialog from "ui/dialogs/progress/ProgressDialog";
import Alert from "ui/dialogs/alert/Alert";
import LoginForm from "ui/components/login-form/LoginForm";

/**
 * @typedef {Object} T_SceneEditorStorageFile
 * @property {string} id
 * @property {number} time
 * @property {FileSystemFileHandle} file
 */

/**
 * @typedef {Object} T_FileData
 * @property {string} id
 * @property {number} time
 * @property {T_CanvasData} data
 */

export default class SceneEditorStorage {
	/**
	 * @param {SceneEditor} editor
	 */
	constructor(editor) {
		/**
		 * @type {SceneEditor}
		 * @private
		 */
		this._editor = editor;

		/**
		 * @type {T_SceneEditorStorageFile|null}
		 * @private
		 */
		this._file = null;

		/**
		 * @type {MainDb}
		 * @private
		 */
		this._db = MainDb.getInstance();
	}

	async load() {
		if (this._editor.sceneId === 'l0') {
			// blank element id
			return;
		}

		Spinner.show();

		/** @type {T_SceneEditorStorageFile} */
		const file = await this._db.get(STORE_FILES, this._editor.sceneId);
		if (file) {
			if (await this._isActualFile(file) === false) {
				await this._loadFromServer();
				Spinner.hide();
				return;
			}

			if (await this._requestFilePermissions(file.file) === false) {
				FatalError.show(i18n('errors.file.no-permission'), '', () => {
					this.load();
				});
				Spinner.hide();
				return;
			}

			const content = await this._readFile(file.file);
			if (content !== null) {
				this._file = file;
				await this._editor.setSceneData(content.data);
			}
			return;
		}

		await this._loadFromServer();
		Spinner.hide();
	}

	/**
	 * @param {boolean} asNew
	 * @return {Promise<void>}
	 */
	async save(asNew = false) {
		let fileHandle;

		if (this._file && !asNew) {
			fileHandle = this._file.file;
		} else {
			try {
				fileHandle = await window.showSaveFilePicker(this._getFilePickerOptions());
			} catch (e) {
				return;
			}
		}

		if (!fileHandle) {
			return;
		}

		if (await this._requestFilePermissions(fileHandle) === false) {
			FatalError.show(i18n('errors.file.no-permission'));
			return;
		}

		if (this._editor.sceneId === 'l0' || asNew) {
			this._editor.sceneId = generateUuid();
		}

		if (await this._saveFile(fileHandle)) {
			this._editor.canvas.markStateAsSaved();
		}
	}

	async open() {
		let fileHandle;

		try {
			[fileHandle] = await window.showOpenFilePicker(this._getFilePickerOptions());
		} catch (e) {
			return;
		}

		const content = await this._readFile(fileHandle);
		if (content === null) {
			return;
		}

		this._file = {
			id: content.id,
			time: content.time,
			file: fileHandle
		};

		this._editor.sceneId = content.id;
		await this._editor.setSceneData(content.data);
		await this._db.set(STORE_FILES, this._file);
	}

	async import() {
		let fileHandle;

		try {
			[fileHandle] = await window.showOpenFilePicker(this._getFilePickerOptions());
		} catch (e) {
			return;
		}

		const content = await this._readFile(fileHandle);
		if (content === null) {
			return;
		}

		const data = await new SceneFlatter().flatCanvasData(content.data);
		this._editor.canvas.addLayer({
			data: data.layers[0]
		});
	}

	async release() {
		if (this._editor.sceneId === 'l0') {
			this._editor.sceneId = generateUuid();
		}

		if (!CurrentUser.get().isUser()) {
			const res = await new LoginForm().getResult();
			if (!res) {
				return;
			}
		}

		const releaseInfo = await new ReleaseForm(this._editor.sceneId, this._editor.canvas).getResultPromise();
		if (!releaseInfo) {
			return;
		}

		if (this._file) {
			await this._saveFile(this._file.file);
		}

		const draftKey = await this._uploadFile(releaseInfo);
		if (!draftKey) {
			return;
		}

		if (CurrentUser.get().isModerator()) {
			const sceneId = await new ProgressDialog({
				title: i18n('title.saving')
			}).load('/editor/release/' + draftKey);

			if (sceneId && typeof sceneId === 'string') {
				this._editor.sceneId = sceneId;
			}
		} else {
			new Alert(i18n('messages.released'))
		}

		this._editor.canvas.markStateAsSaved();
	}

	/**
	 * @param {T_ReleaseData} releaseInfo
	 * @return {Promise<string>}
	 * @private
	 */
	async _uploadFile(releaseInfo) {
		Spinner.show();
		let res;
		const data = await this._editor.canvas.getData(false);

		try {
			res = await Ajax.post('/editor/upload/' + this._editor.sceneId, {
				id: this._editor.sceneId,
				data: JSON.stringify(data)
			});

			if (res.result !== 'ok') {
				FatalError.show(res.error);
				Spinner.hide();
				return '';
			}
		} catch (e) {
			FatalError.show(typeof e === 'string' ? e : i18n('errors.http.error'));
			Spinner.hide();
			return '';
		}

		Spinner.hide();
		return res.key || '';
	}

	/**
	 * @return {Promise<void>}
	 * @private
	 */
	async _loadFromServer() {
		if (!this._isServerLoadAllowed()) {
			// cannot load file from server, then reset scene ID to blank
			this._editor.sceneId = 'l0';
			return;
		}

		let data;
		try {
			data = await Ajax.get('/editor/load/' + this._editor.sceneId);
		} catch (msg) {
			FatalError.show(msg || i18n('errors.http.server-issue'), '/editor');
			return;
		}

		await this._editor.setSceneData(data.data);
	}

	/**
	 * @param {T_SceneEditorStorageFile} file
	 * @returns {boolean}
	 * @private
	 */
	async _isActualFile(file) {
		const res = await new Confirm(i18n('messages.confirm-file-load')).getResult();
		Spinner.show();
		return res;
	}

	/**
	 * @return {boolean}
	 * @private
	 */
	_isServerLoadAllowed() {
		return !isUuid(this._editor.sceneId)
			|| CurrentUser.get().isUser();
	}

	/**
	 * @return {object}
	 * @private
	 */
	_getFilePickerOptions() {
		return {
			types: [
				{
					description: 'Floor796 Scene',
					accept: {
						'application/floor-796': ['.796'],
					},
				},
			],
		};
	}

	/**
	 * @param {FileSystemFileHandle} fileHandle
	 * @return {Promise<T_FileData|null>}
	 * @private
	 */
	async _readFile(fileHandle) {
		let content;

		try {
			const file = await fileHandle.getFile();
			content = await file.text();
			content = JSON.parse(content);
		} catch (e) {
			console.error(e.message);
			content = null;
		}

		if (typeof content !== 'object' || content === null || typeof content.id !== 'string') {
			FatalError.show(i18n('errors.file.invalid-content'));
		}

		return content;
	}

	/**
	 * @param fileHandle
	 * @return {Promise<boolean>}
	 * @private
	 */
	async _saveFile(fileHandle) {
		this._file = {
			id: this._editor.sceneId,
			time: Date.now(),
			file: fileHandle
		};

		const writable = await fileHandle.createWritable();
		try {
			await writable.write(JSON.stringify({
				id: this._file.id,
				time: this._file.time,
				data: await this._editor.canvas.getData(false)
			}));
			writable.close();
		} catch (e) {
			return false;
		}

		await this._db.set(STORE_FILES, this._file);
		return true;
	}

	/**
	 * @param {FileSystemFileHandle} fileHandle
	 * @return {Promise<boolean>}
	 * @private
	 */
	async _requestFilePermissions(fileHandle) {
		const opts = {mode: 'readwrite'};
		const perm = await fileHandle.queryPermission(opts);
		if (perm === 'prompt') {
			if (await fileHandle.requestPermission(opts) !== 'granted') {
				return false;
			}
		} else if (perm === 'denied') {
			return false;
		}

		return true;
	}
}
